/**
 * Landing Bridge Mediator 实现
 *
 * React 层通过 register() 注册各能力的处理 Strategy，
 * Section JS 通过 Facade 方法（navigate / requireLogin / …）调用。
 * 未注册的能力会 fallback 到安全的 NOOP 默认行为。
 */
import type {
  BridgeGeneratorConfig,
  BridgeSiteConfig,
  BridgeUploadConfig,
  BridgeUploadResult,
  BridgeUserInfo,
  LandingBridge,
  LandingBridgeMediator,
} from './types';

const BRIDGE_VERSION = '1.0.0';

export class LandingBridgeImpl implements LandingBridge, LandingBridgeMediator {
  private implementations: Partial<Record<keyof LandingBridge, unknown>> = {};

  readonly version = BRIDGE_VERSION;

  /** Strategy 注册入口 */
  register<K extends keyof LandingBridge>(key: K, impl: LandingBridge[K]): void {
    this.implementations[key] = impl;
  }

  // ─── Facade API ────────────────────────────────────────

  navigate(path: string, options?: { replace?: boolean }): void {
    const fn = this.implementations.navigate as LandingBridge['navigate'] | undefined;
    if (fn) {
      fn(path, options);
    } else {
      console.warn('[LandingBridge] navigate not registered, fallback to location.href');
      window.location.href = path;
    }
  }

  getConfig(): BridgeSiteConfig {
    const fn = this.implementations.getConfig as LandingBridge['getConfig'] | undefined;
    if (fn) return fn();
    return { siteUrl: '', routes: {} };
  }

  requireLogin(callback?: (user: BridgeUserInfo) => void): void {
    const fn = this.implementations.requireLogin as
      | LandingBridge['requireLogin']
      | undefined;
    if (fn) {
      fn(callback);
    } else {
      console.warn('[LandingBridge] requireLogin not registered');
    }
  }

  isLoggedIn(): boolean {
    const fn = this.implementations.isLoggedIn as LandingBridge['isLoggedIn'] | undefined;
    return fn ? fn() : false;
  }

  openUpload(
    config: BridgeUploadConfig,
    callback: (result: BridgeUploadResult) => void,
  ): void {
    const fn = this.implementations.openUpload as LandingBridge['openUpload'] | undefined;
    if (fn) {
      fn(config, callback);
    } else {
      callback({ success: false, error: 'upload not registered' });
    }
  }

  track(event: string, data?: Record<string, any>): void {
    const fn = this.implementations.track as LandingBridge['track'] | undefined;
    if (fn) fn(event, data);
    // track 静默失败，不 warn
  }

  submitToGenerator(config: BridgeGeneratorConfig): void {
    const fn = this.implementations.submitToGenerator as
      | LandingBridge['submitToGenerator']
      | undefined;
    if (fn) {
      fn(config);
    } else {
      console.warn('[LandingBridge] submitToGenerator not registered');
      config.onError?.('submitToGenerator not registered');
    }
  }
}
